/**
 * Componente de cards de estatísticas para logs de auditoria
 * Design minimalista e profissional utilizando o componente StatCard
 */

import { useMemo } from 'react';
import { StatCard } from '@/components/ui/StatCard';
import { AuditLog } from '@/services/AuditService';

interface LogsStatsCardsProps {
  logs: AuditLog[];
  isLoading?: boolean;
}

export function LogsStatsCards({
  logs,
  isLoading = false,
}: LogsStatsCardsProps) {
  // Cálculo das estatísticas
  const stats = useMemo(() => {
    if (!logs || logs.length === 0) {
      return {
        total: 0,
        today: 0,
        thisWeek: 0,
        activeUsers: 0,
      };
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const todayLogs = logs.filter((log) => new Date(log.created_at) >= today);
    const weekLogs = logs.filter((log) => new Date(log.created_at) >= weekAgo);
    
    const uniqueUsers = new Set(
      logs.map((log) => log.profiles?.id || log.profile_id || 'system')
    ).size;

    return {
      total: logs.length,
      today: todayLogs.length,
      thisWeek: weekLogs.length,
      activeUsers: uniqueUsers,
    };
  }, [logs]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="bg-card rounded-xl p-6 border border-border animate-pulse h-[120px]"
          />
        ))}
      </div>
    );
  }

  const cards = [
    {
      title: 'Total de Logs',
      value: stats.total.toLocaleString(),
      subtitle: 'Histórico completo',
      trend: null,
    },
    {
      title: 'Logs Hoje',
      value: stats.today.toLocaleString(),
      subtitle: 'Últimas 24 horas',
      trend: stats.today > 0 ? 'positive' : 'neutral',
      trendLabel: stats.today > 0 ? 'Ativo' : 'Sem atividade',
    },
    {
      title: 'Esta Semana',
      value: stats.thisWeek.toLocaleString(),
      subtitle: 'Últimos 7 dias',
      trend: null,
    },
    {
      title: 'Usuários Ativos',
      value: stats.activeUsers.toLocaleString(),
      subtitle: 'Usuários únicos',
      trend: null,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {cards.map((card, index) => (
        <StatCard
          key={index}
          title={card.title}
          value={card.value}
          subtitle={card.subtitle}
          trend={card.trend as any}
          trendLabel={card.trendLabel}
        />
      ))}
    </div>
  );
}
